import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { apiService } from '../services/apiService';
import JapanAddressSelector from './JapanAddressSelector';
import AttachmentPreviewModal from './AttachmentPreviewModal';
import ClientSelectionFields from './ClientSelectionFields';
import { useFilePreview } from '../hooks/useFilePreview';
import { isSpecifiedSkill2Talent } from '../utils/residenceStatus';
import { useAuth } from '../context/AuthContext';

// 可复用的表单项组件
const EditItem = ({ label, name, value, onChange, type = 'text', required = false, inputMode, pattern, maxLength, disabled = false }) => {
    // Set date range limits for date inputs to restrict year to 4 digits
    const dateProps = type === 'date' ? {
        min: '1900-01-01',
        max: '2099-12-31'
    } : {};

    return (
        <div>
            <label htmlFor={name} className="block text-sm font-medium text-gray-700">
                {label}{required && '*'}
            </label>
            <input
                type={type}
                name={name}
                id={name}
                value={value || ''}
                onChange={onChange}
                required={required}
                inputMode={inputMode}
                pattern={pattern}
                maxLength={maxLength}
                disabled={disabled}
                {...dateProps}
                className={`mt-1 block w-full border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-2 focus:ring-indigo-500 ${disabled ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''}`}
            />
        </div>
    );
};

// 新增：可复用的下拉选择框组件
const SelectItem = ({ label, name, value, onChange, required = false, options, optionType, t, getTranslatedOption, disabled = false }) => {
    const handleClick = (e) => {
        console.log('SelectItem clicked, name:', name, 'currentValue:', value, 'target:', e.target);
    };

    return (
        <div>
            <label htmlFor={name} className="block text-sm font-medium text-gray-700">
                {label}{required && '*'}
            </label>
            <select
                name={name}
                id={name}
                value={value || ''}
                onChange={onChange}
                onClick={handleClick}
                required={required}
                disabled={disabled}
                className={`mt-1 block w-full border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-2 focus:ring-indigo-500 ${disabled ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''}`}
            >
                <option value="" disabled>{t('pleaseSelect')}</option>
                {options.map(option => {
                    const optionValue = typeof option === 'object' ? option.value : option;
                    const optionLabel = typeof option === 'object' ? option.label : option;

                    return (
                        <option key={optionValue} value={optionValue}>
                            {getTranslatedOption ? getTranslatedOption(optionLabel, optionType) : optionLabel}
                        </option>
                    );
                })}
            </select>
        </div>
    );
};

const normalizePauseDate = (value) => {
    if (!value) return '';
    if (typeof value === 'string') {
        return value.split('T')[0];
    }
    return value;
};

const mapPauseRowsFromApi = (rows, talentSnapshot) => {
    const mapped = (Array.isArray(rows) ? rows : []).map((row, index) => ({
        id: row.id || `pause-existing-${index}`,
        pauseType: row.pauseType || "育休",
        startDate: normalizePauseDate(row.startDate),
        endDate: normalizePauseDate(row.endDate)
    }));

    if (mapped.length > 0) {
        return mapped;
    }

    const snapshotType = (talentSnapshot?.employmentPauseType || '').trim();
    if ((snapshotType === '育休' || snapshotType === '一時帰国') && talentSnapshot?.employmentPauseStartDate) {
        return [{
            id: 'pause-legacy',
            pauseType: snapshotType,
            startDate: normalizePauseDate(talentSnapshot.employmentPauseStartDate),
            endDate: normalizePauseDate(talentSnapshot.employmentPauseEndDate)
        }];
    }

    return [];
};

// ✅【修改处】: 删除了未被使用的 TextAreaItem 组件定义

export default function CreateOrEditTalentModal({ isOpen, onClose, onFinished, existingTalent }) {
    const { t } = useTranslation();
    const { user } = useAuth();
    const attachmentPreview = useFilePreview(apiService.downloadAttachment);
    const [formData, setFormData] = useState({});
    const [employmentPauseHistory, setEmploymentPauseHistory] = useState([]);
    const [filesToUpload, setFilesToUpload] = useState([]);
    const [existingAttachments, setExistingAttachments] = useState([]);
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [customFieldValue, setCustomFieldValue] = useState('');
    const [showCustomField, setShowCustomField] = useState(false);
    const [salesUsers, setSalesUsers] = useState([]);
    const [isSalesUsersLoading, setIsSalesUsersLoading] = useState(false);
    const [salesUsersLoadError, setSalesUsersLoadError] = useState('');

    // Photo-specific state
    const [photoToUpload, setPhotoToUpload] = useState(null);
    const [existingPhoto, setExistingPhoto] = useState(null);
    const [photoPreviewUrl, setPhotoPreviewUrl] = useState(null);

    // Original values for data consistency 
    const genderOptions = useMemo(() => ["男", "女"], []);
    const nationalityOptions = useMemo(() => ["中国", "韩国", "缅甸", "印度尼西亚", "越南", "菲律宾"], []);
    const fieldOptions = useMemo(() => ["外食", "飲食料品製造", "溶接", "介護", "他"], []);
    const employmentStatusOptions = useMemo(() => ["内定", "申請中", "在職", "離職", "待入国"], []);
    const billingPlanTypeOptions = useMemo(() => ["通常", "初期費+Nヶ月管理費", "段階管理費"], []);
    const pauseTypeOptions = useMemo(() => ["育休", "一時帰国"], []);
    const isAdmin = user?.role === 'Admin' || user?.role === 'SuperAdmin';
    const isAdminCreating = isAdmin && !existingTalent;
    const createPauseItem = () => ({
        id: `pause-${Date.now()}-${Math.random()}`,
        pauseType: "育休",
        startDate: '',
        endDate: ''
    });

    const sanitizePauseRows = (rows) => {
        return (Array.isArray(rows) ? rows : [])
            .map(row => ({
                pauseType: (row.pauseType || '').trim(),
                startDate: normalizePauseDate(row.startDate),
                endDate: normalizePauseDate(row.endDate) || null
            }))
            .filter(row => (row.pauseType === '育休' || row.pauseType === '一時帰国') && row.startDate);
    };

    const resolveCurrentPauseSnapshot = (rows, employmentStatus) => {
        if (employmentStatus !== '在職') {
            return { pauseType: 'なし', startDate: null, endDate: null };
        }

        const today = new Date().toISOString().slice(0, 10);
        const activeRow = rows
            .filter(row => row.startDate && row.startDate <= today)
            .filter(row => !row.endDate || row.endDate >= today)
            .sort((a, b) => (a.startDate < b.startDate ? 1 : -1))[0];

        if (!activeRow) {
            return { pauseType: 'なし', startDate: null, endDate: null };
        }

        return {
            pauseType: activeRow.pauseType,
            startDate: activeRow.startDate,
            endDate: activeRow.endDate || null
        };
    };

    // Translation mapping for display
    const getTranslatedOption = (value, type) => {
        const translations = {
            gender: { "男": t('male'), "女": t('female') },
            nationality: {
                "中国": t('chinese'),
                "韩国": t('korean'),
                "韓国": t('korean'),
                "缅甸": t('myanmar'),
                "ミャンマー": t('myanmar'),
                "印度尼西亚": t('indonesia'),
                "インドネシア": t('indonesia'),
                "越南": t('vietnamese'),
                "ベトナム": t('vietnamese'),
                "台湾": t('taiwan'),
                "菲律宾": t('filipino'),
                "フィリピン": t('filipino')
            },
            field: {
                "外食": t('restaurant'),
                "飲食料品製造": t('foodManufacturing'),
                "溶接": t('welding'),
                "介護": t('care'),
                "他": t('otherField')
            },
            employmentStatus: {
                "内定": t('jobOffer'),
                "申請中": t('applying'),
                "在職": t('employed'),
                "離職": t('unemployed'),
                "待入国": t('awaitingEntry')
            },
            employmentPauseType: {
                "なし": t('employmentPauseTypeNone'),
                "育休": t('employmentPauseTypeChildcare'),
                "一時帰国": t('employmentPauseTypeTemporaryReturn')
            },
            billingPlanType: {
                "通常": t('billingPlanTypeNormal'),
                "初期費+Nヶ月管理費": t('billingPlanTypeInitialPlus'),
                "段階管理費": t('billingPlanTypeTiered')
            }
        };
        return translations[type]?.[value] || value;
    };

    useEffect(() => {
        if (!isOpen || !isAdminCreating) {
            setSalesUsers([]);
            setSalesUsersLoadError('');
            return;
        }

        let isMounted = true;
        setIsSalesUsersLoading(true);
        setSalesUsersLoadError('');

        apiService.getSalesUsers()
            .then((users) => {
                if (isMounted) {
                    setSalesUsers(Array.isArray(users) ? users : []);
                }
            })
            .catch(() => {
                if (isMounted) {
                    setSalesUsers([]);
                    setSalesUsersLoadError(t('loadSalesUsersFailed'));
                }
            })
            .finally(() => {
                if (isMounted) {
                    setIsSalesUsersLoading(false);
                }
            });

        return () => {
            isMounted = false;
        };
    }, [isOpen, isAdminCreating, t]);

    useEffect(() => {
        if (isOpen) {
            const defaultEmploymentStatus = '内定'; // Default to 内定 for new talents
            const initialData = existingTalent
                ? {
                    ...existingTalent,
                    employmentStatus: existingTalent.employmentStatus || defaultEmploymentStatus,
                    billingPlanType: existingTalent.billingPlanType || "通常",
                    employmentPauseType: existingTalent.employmentPauseType || "なし",
                    initialCostNotCharged: Boolean(existingTalent.initialCostNotCharged),
                    managementFeeNotCharged: Boolean(existingTalent.managementFeeNotCharged)
                }
                : {
                    dateOfBirth: '',
                    gender: '',
                    nationality: '',
                    field: '',
                    employmentStatus: defaultEmploymentStatus,
                    billingPlanType: "通常",
                    initialCostNotCharged: false,
                    managementFeeNotCharged: false
                };
            setFormData(initialData);
            setFilesToUpload([]);
            setEmploymentPauseHistory([]);
            setError('');

            // Reset photo state when switching talents
            setPhotoToUpload(null);
            setExistingPhoto(null);
            if (photoPreviewUrl) {
                URL.revokeObjectURL(photoPreviewUrl);
            }
            setPhotoPreviewUrl(null);

            // 处理自定义分野值
            const standardFields = ["外食", "飲食料品製造", "溶接", "介護", "他"];
            console.log('useEffect initializing - existingTalent.field:', existingTalent?.field);
            if (existingTalent && existingTalent.field && !standardFields.includes(existingTalent.field)) {
                // 如果是自定义值，显示自定义输入框，设置自定义值，保持field为实际值
                console.log('Setting showCustomField=true, customFieldValue=', existingTalent.field);
                setShowCustomField(true);
                setCustomFieldValue(existingTalent.field);
                // 不修改formData中的field值，保持原始自定义值
            } else {
                console.log('Setting showCustomField=false');
                setShowCustomField(false);
                setCustomFieldValue('');
            }
            if (existingTalent && existingTalent.id) {
                Promise.all([
                    apiService.getAttachmentsForTalent(existingTalent.id),
                    apiService.getTalentEmploymentPauses(existingTalent.id).catch(() => ({ pauses: [] }))
                ])
                    .then(([attachmentsData, pauseData]) => {
                        const salesAttachments = attachmentsData.filter(att => att.sourceModule === "Sales");
                        const photoAttachments = attachmentsData.filter(att => att.sourceModule === "Photo");

                        setExistingAttachments(salesAttachments);
                        setEmploymentPauseHistory(mapPauseRowsFromApi(pauseData?.pauses, existingTalent));

                        // Handle existing photo
                        if (photoAttachments.length > 0) {
                            const photo = photoAttachments[0]; // Use first photo
                            setExistingPhoto(photo);
                            // Load photo preview
                            apiService.downloadAttachment(photo.id)
                                .then(blob => {
                                    const url = URL.createObjectURL(blob);
                                    setPhotoPreviewUrl(url);
                                })
                                .catch(err => console.error('Failed to load photo:', err));
                        } else {
                            setExistingPhoto(null);
                            setPhotoPreviewUrl(null);
                        }
                    })
                    .catch(err => setError(t('loadFailed') + ': ' + err.message));
            } else {
                setExistingAttachments([]);
                setEmploymentPauseHistory([]);
            }
        }
    }, [isOpen, existingTalent, t, photoPreviewUrl]);

    // Cleanup photo preview URL on unmount
    useEffect(() => {
        return () => {
            if (photoPreviewUrl) {
                URL.revokeObjectURL(photoPreviewUrl);
            }
        };
    }, [photoPreviewUrl]);

    if (!isOpen) return null;

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        const resolvedValue = type === 'checkbox' ? checked : value;
        console.log('handleChange:', name, value, 'showCustomField:', showCustomField, 'formData.field:', formData.field);
        // 🔧 管理番号格式限制已移除 - 现在可以输入任意字符
        if (name === 'initialCostNotCharged') {
            const nextData = { ...formData, initialCostNotCharged: Boolean(resolvedValue) };
            if (nextData.initialCostNotCharged) {
                nextData.initialCost = '';
            }
            setFormData(nextData);
            return;
        }
        if (name === 'managementFeeNotCharged') {
            const nextData = { ...formData, managementFeeNotCharged: Boolean(resolvedValue) };
            if (nextData.managementFeeNotCharged) {
                nextData.managementFee = 0;
                nextData.firstMonthManagementFee = '';
                nextData.includedManagementMonths = '';
                nextData.tier1Months = '';
                nextData.tier1ManagementFee = '';
                nextData.tier2Months = '';
                nextData.tier2ManagementFee = '';
                nextData.billingPlanType = '通常';
            }
            setFormData(nextData);
            return;
        }
        if (name === 'employmentStatus') {
            const nextData = { ...formData, employmentStatus: value };
            if (value !== '在職') {
                nextData.employmentPauseType = 'なし';
                nextData.employmentPauseStartDate = '';
                nextData.employmentPauseEndDate = '';
            }
            setFormData(nextData);
            return;
        }
        if (name === 'billingPlanType') {
            if (formData.managementFeeNotCharged) {
                return;
            }
            const nextData = { ...formData, billingPlanType: value };
            if (value === '通常') {
                nextData.includedManagementMonths = '';
                nextData.tier1Months = '';
                nextData.tier1ManagementFee = '';
                nextData.tier2Months = '';
                nextData.tier2ManagementFee = '';
            } else if (value === '初期費+Nヶ月管理費') {
                nextData.tier1Months = '';
                nextData.tier1ManagementFee = '';
                nextData.tier2Months = '';
                nextData.tier2ManagementFee = '';
            } else if (value === '段階管理費') {
                nextData.includedManagementMonths = '';
            }
            setFormData(nextData);
            return;
        }
        if (name === 'field') {
            if (value === '他') {
                // 选择"他"时，显示自定义字段输入框
                console.log('Setting showCustomField to true');
                setShowCustomField(true);
                // 如果之前已经是自定义值，保持原值；否则清空
                if (!showCustomField) {
                    setCustomFieldValue(''); // 从标准选项切换到"他"时,清空自定义值
                    setFormData({ ...formData, [name]: '' });
                }
                // 如果已经是showCustomField=true,说明已经有自定义值,不做任何改变
            } else {
                // 选择其他选项时，隐藏自定义字段输入框，清空自定义字段值
                console.log('Setting showCustomField to false, value=', value);
                setShowCustomField(false);
                setCustomFieldValue('');
                setFormData({ ...formData, [name]: value });
            }
        } else {
            setFormData({ ...formData, [name]: value });
        }
    };

    const handlePauseRowChange = (id, field, value) => {
        setEmploymentPauseHistory(prev => prev.map(row => (
            row.id === id ? { ...row, [field]: value } : row
        )));
    };

    const handleAddPauseRow = () => {
        setEmploymentPauseHistory(prev => [...prev, createPauseItem()]);
    };

    const handleRemovePauseRow = (id) => {
        setEmploymentPauseHistory(prev => prev.filter(row => row.id !== id));
    };

    const handleCustomFieldChange = (e) => {
        const value = e.target.value;
        setCustomFieldValue(value);
        // 当用户在自定义输入框中输入时，更新field值
        // 即使输入框为空，也保持showCustomField=true，不关闭输入框
        setFormData({ ...formData, field: value || '' });
    };

    const handleClientSelectionChange = (patch) => {
        setFormData(prev => ({
            ...prev,
            ...patch
        }));
    };

    const handleFileChange = (e) => {
        setFilesToUpload([...e.target.files]);
        e.target.value = '';
    };

    const handleDeleteAttachment = async (attachmentId) => {
        if (window.confirm(t('confirmDelete'))) {
            try {
                await apiService.deleteAttachment(attachmentId);
                setExistingAttachments(prev => prev.filter(att => att.id !== attachmentId));
            } catch (err) {
                setError(t('deleteFailed') + ': ' + err.message);
            }
        }
    };

    const handleDownloadAttachment = async (attachmentId, fileName) => {
        try {
            const blob = await apiService.downloadAttachment(attachmentId);
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = fileName;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            setError(t('downloadFailed') + ': ' + err.message);
        }
    };

    // Photo handlers
    const handlePhotoChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            // Validate file type
            if (!file.type.startsWith('image/')) {
                setError(t('onlyImagesAllowed') || '画像ファイルのみアップロード可能です');
                return;
            }

            // Validate file size (e.g., max 5MB)
            if (file.size > 5 * 1024 * 1024) {
                setError(t('fileTooLarge') || 'ファイルサイズが大きすぎます（最大5MB）');
                return;
            }

            setPhotoToUpload(file);

            // Create preview URL
            const url = URL.createObjectURL(file);
            if (photoPreviewUrl) {
                URL.revokeObjectURL(photoPreviewUrl);
            }
            setPhotoPreviewUrl(url);
        }
    };

    const handleDeletePhoto = async () => {
        if (existingPhoto && window.confirm(t('confirmDelete'))) {
            try {
                await apiService.deleteAttachment(existingPhoto.id);
                setExistingPhoto(null);
                if (photoPreviewUrl) {
                    URL.revokeObjectURL(photoPreviewUrl);
                }
                setPhotoPreviewUrl(null);
            } catch (err) {
                setError(t('deleteFailed') + ': ' + err.message);
            }
        } else if (photoToUpload) {
            // Just clear the selected photo
            setPhotoToUpload(null);
            if (photoPreviewUrl) {
                URL.revokeObjectURL(photoPreviewUrl);
            }
            setPhotoPreviewUrl(null);
        }
    };

    const handleDownloadPhoto = async () => {
        if (existingPhoto) {
            try {
                const blob = await apiService.downloadAttachment(existingPhoto.id);
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = existingPhoto.fileName || 'photo.jpg';
                document.body.appendChild(a);
                a.click();
                a.remove();
                window.URL.revokeObjectURL(url);
            } catch (err) {
                setError(t('downloadFailed') + ': ' + err.message);
            }
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setError('');
        try {
            // 清理数据：移除导航属性和其他可能导致问题的字段
            const cleanedData = { ...formData };

            if (isAdminCreating && !cleanedData.salesStaffId) {
                throw new Error(t('responsibleSalesRequired'));
            }

            // 移除导航属性，防止验证错误
            delete cleanedData.salesStaff;
            delete cleanedData.residencePermit;
            delete cleanedData.loan;
            delete cleanedData.attachments;
            delete cleanedData.employmentPauses;
            delete cleanedData.clientOrganization;
            delete cleanedData.clientWorkplace;

            // 处理日期字段 - 如果为空则设为null
            if (!cleanedData.dateOfBirth || cleanedData.dateOfBirth === '') {
                cleanedData.dateOfBirth = null;
            }
            if (!cleanedData.contractEndDate || cleanedData.contractEndDate === '') {
                cleanedData.contractEndDate = null;
            }

            // 🔧 确保数值字段是数字类型
            cleanedData.initialCostNotCharged = Boolean(cleanedData.initialCostNotCharged);
            cleanedData.managementFeeNotCharged = Boolean(cleanedData.managementFeeNotCharged);

            if (cleanedData.initialCostNotCharged || cleanedData.initialCost === '' || cleanedData.initialCost === null || cleanedData.initialCost === undefined) {
                cleanedData.initialCost = null;
            } else {
                cleanedData.initialCost = parseFloat(cleanedData.initialCost);
                if (Number.isNaN(cleanedData.initialCost)) {
                    cleanedData.initialCost = null;
                }
            }

            if (cleanedData.introductionFee === '' || cleanedData.introductionFee === null || cleanedData.introductionFee === undefined) {
                cleanedData.introductionFee = null;
            } else {
                cleanedData.introductionFee = parseFloat(cleanedData.introductionFee);
                if (Number.isNaN(cleanedData.introductionFee)) {
                    cleanedData.introductionFee = null;
                }
            }

            if (cleanedData.managementFeeNotCharged) {
                cleanedData.managementFee = 0;
                cleanedData.firstMonthManagementFee = null;
                cleanedData.includedManagementMonths = null;
                cleanedData.tier1Months = null;
                cleanedData.tier1ManagementFee = null;
                cleanedData.tier2Months = null;
                cleanedData.tier2ManagementFee = null;
                cleanedData.billingPlanType = '通常';
            } else {
                cleanedData.managementFee = parseFloat(cleanedData.managementFee) || 0;
                // FirstMonthManagementFee 是可选字段，如果为空设为null
                if (cleanedData.firstMonthManagementFee) {
                    cleanedData.firstMonthManagementFee = parseFloat(cleanedData.firstMonthManagementFee);
                } else {
                    cleanedData.firstMonthManagementFee = null;
                }
            }

            const parseIntOrNull = (value) => {
                if (value === '' || value === null || value === undefined) return null;
                const parsed = parseInt(value, 10);
                return Number.isNaN(parsed) ? null : parsed;
            };
            const parseDecimalOrNull = (value) => {
                if (value === '' || value === null || value === undefined) return null;
                const parsed = parseFloat(value);
                return Number.isNaN(parsed) ? null : parsed;
            };

            cleanedData.includedManagementMonths = parseIntOrNull(cleanedData.includedManagementMonths);
            cleanedData.tier1Months = parseIntOrNull(cleanedData.tier1Months);
            cleanedData.tier2Months = parseIntOrNull(cleanedData.tier2Months);
            cleanedData.tier1ManagementFee = parseDecimalOrNull(cleanedData.tier1ManagementFee);
            cleanedData.tier2ManagementFee = parseDecimalOrNull(cleanedData.tier2ManagementFee);

            const normalizedPauseRows = sanitizePauseRows(employmentPauseHistory);
            const invalidPause = normalizedPauseRows.find(row => row.endDate && row.endDate < row.startDate);
            if (invalidPause) {
                throw new Error(t('employmentPauseDateRangeError'));
            }

            // 确保employmentStatus有值（但不要覆盖用户选择的值）
            if (!cleanedData.employmentStatus || cleanedData.employmentStatus === '') {
                cleanedData.employmentStatus = '内定'; // Default to 内定 for new talents
            }
            if (!cleanedData.billingPlanType || cleanedData.billingPlanType === '') {
                cleanedData.billingPlanType = '通常';
            }
            const currentPauseSnapshot = resolveCurrentPauseSnapshot(normalizedPauseRows, cleanedData.employmentStatus);
            cleanedData.employmentPauseType = currentPauseSnapshot.pauseType;
            cleanedData.employmentPauseStartDate = currentPauseSnapshot.startDate;
            cleanedData.employmentPauseEndDate = currentPauseSnapshot.endDate;

            // 调试信息
            console.log('提交的数据:', {
                employmentStatus: cleanedData.employmentStatus,
                fullName: cleanedData.fullName,
                operation: existingTalent ? 'update' : 'create'
            });

            let talentId;
            if (existingTalent) {
                await apiService.updateTalent(existingTalent.id, cleanedData);
                talentId = existingTalent.id;
            } else {
                const newTalent = await apiService.createTalent(cleanedData);
                talentId = newTalent.id;
            }

            if (talentId) {
                await apiService.updateTalentEmploymentPauses(talentId, normalizedPauseRows);
            }

            // Upload photo first if selected
            if (photoToUpload) {
                if (!talentId) {
                    throw new Error(t('uploadFailed'));
                }
                // Delete existing photo first if exists
                if (existingPhoto) {
                    await apiService.deleteAttachment(existingPhoto.id);
                }
                // Upload new photo with "Photo" module type
                await apiService.uploadAttachment(talentId, photoToUpload, "Photo");
            }

            // Upload other files
            if (filesToUpload.length > 0) {
                if (!talentId) {
                    throw new Error(t('uploadFailed'));
                }
                for (const file of filesToUpload) {
                    await apiService.uploadAttachment(talentId, file, "Sales");
                }
            }

            onFinished();
            onClose();
        } catch (err) {
            setError(err.message || t('saveFailed'));
        } finally {
            setIsLoading(false);
        }
    };

    const title = existingTalent ? t('editTalent') : t('createTalent');
    const isSpecifiedSkill2 = isSpecifiedSkill2Talent(existingTalent);

    return (
        <>
        <div className="modal-container fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-4">
            <div className="modal-content bg-white rounded-lg shadow-xl w-full max-w-4xl">
                <form onSubmit={handleSubmit}>
                    <div className="modal-header p-6 border-b">
                        <div className="flex items-center justify-between">
                            <h2 className="text-2xl font-bold text-gray-800">{title}</h2>
                            <button
                                type="button"
                                onClick={onClose}
                                className="text-gray-400 hover:text-gray-600 transition-colors"
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                    </div>
                    <div className="modal-body p-6 max-h-[70vh] overflow-y-auto">
                        <div className="flex">
                            {/* 左侧表单字段 */}
                            <div className="flex-1 space-y-4">
                                <EditItem label={t('fullName')} name="fullName" value={formData.fullName} onChange={handleChange} required />
                                <EditItem label={t('fullNameFurigana')} name="fullNameFurigana" value={formData.fullNameFurigana} onChange={handleChange} />
                                <EditItem
                                    label={t('managementNo')}
                                    name="managementNumber"
                                    value={formData.managementNumber}
                                    onChange={handleChange}
                                    maxLength={100}
                                />

                                {isAdminCreating && (
                                    <div className="rounded-md border border-indigo-100 bg-indigo-50/60 p-3">
                                        <SelectItem
                                            label={t('responsibleSales')}
                                            name="salesStaffId"
                                            value={formData.salesStaffId}
                                            onChange={handleChange}
                                            required
                                            options={salesUsers.map((salesUser) => ({
                                                value: salesUser.id,
                                                label: salesUser.username
                                            }))}
                                            t={t}
                                            disabled={isSalesUsersLoading || salesUsers.length === 0}
                                        />
                                        {isSalesUsersLoading && (
                                            <p className="mt-2 text-xs text-indigo-700">{t('loading')}</p>
                                        )}
                                        {salesUsersLoadError && (
                                            <p className="mt-2 text-xs text-red-700">{salesUsersLoadError}</p>
                                        )}
                                        {!isSalesUsersLoading && !salesUsersLoadError && salesUsers.length === 0 && (
                                            <p className="mt-2 text-xs text-red-700">{t('responsibleSalesRequired')}</p>
                                        )}
                                    </div>
                                )}

                                <SelectItem label={t('gender')} name="gender" value={formData.gender} onChange={handleChange} options={genderOptions} optionType="gender" t={t} getTranslatedOption={getTranslatedOption} />
                                <SelectItem label={t('nationality')} name="nationality" value={formData.nationality} onChange={handleChange} options={nationalityOptions} optionType="nationality" t={t} getTranslatedOption={getTranslatedOption} />

                                {/* 分野选择 - 带自定义选项切换按钮 */}
                                <div>
                                    <div className="flex items-end gap-2">
                                        <div className="flex-1">
                                            <SelectItem label={t('field')} name="field" value={showCustomField ? '他' : (formData.field || '')} onChange={handleChange} options={fieldOptions} optionType="field" t={t} getTranslatedOption={getTranslatedOption} />
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                console.log('Custom field button clicked, current showCustomField:', showCustomField);
                                                if (!showCustomField) {
                                                    // 切换到自定义模式
                                                    setShowCustomField(true);
                                                    setCustomFieldValue('');
                                                    setFormData({ ...formData, field: '' });
                                                } else {
                                                    // 切换回标准模式
                                                    setShowCustomField(false);
                                                    setCustomFieldValue('');
                                                    setFormData({ ...formData, field: '' });
                                                }
                                            }}
                                            className={`px-4 py-2 rounded-md transition-colors ${showCustomField
                                                    ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                                                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                                }`}
                                        >
                                            {showCustomField ? '✓ カスタム' : 'カスタム'}
                                        </button>
                                    </div>
                                    {showCustomField && (
                                        <div className="mt-2">
                                            <EditItem label={t('customField')} name="customField" value={customFieldValue} onChange={handleCustomFieldChange} placeholder={t('pleaseEnterCustomField')} required />
                                        </div>
                                    )}
                                </div>
                                <SelectItem label={t('employmentStatus')} name="employmentStatus" value={formData.employmentStatus} onChange={handleChange} options={employmentStatusOptions} optionType="employmentStatus" t={t} getTranslatedOption={getTranslatedOption} required />

                                <div className="border border-gray-200 rounded-md p-3">
                                    <div className="flex justify-between items-center mb-3">
                                        <h4 className="text-sm font-semibold text-gray-700">{t('employmentPauseType')}履歴</h4>
                                        <button
                                            type="button"
                                            onClick={handleAddPauseRow}
                                            className="px-3 py-1 text-xs bg-indigo-600 text-white rounded hover:bg-indigo-700"
                                        >
                                            + 追加
                                        </button>
                                    </div>
                                    {employmentPauseHistory.length === 0 ? (
                                        <p className="text-xs text-gray-500">{t('none') || 'なし'}</p>
                                    ) : (
                                        <div className="space-y-2">
                                            {employmentPauseHistory.map((row) => (
                                                <div key={row.id} className="grid grid-cols-4 gap-2 items-center">
                                                    <select
                                                        value={row.pauseType || '育休'}
                                                        onChange={(e) => handlePauseRowChange(row.id, 'pauseType', e.target.value)}
                                                        className="border-gray-300 rounded-md shadow-sm py-2 px-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                                    >
                                                        {pauseTypeOptions.map(option => (
                                                            <option key={option} value={option}>
                                                                {getTranslatedOption(option, 'employmentPauseType')}
                                                            </option>
                                                        ))}
                                                    </select>
                                                    <input
                                                        type="date"
                                                        min="1900-01-01"
                                                        max="2099-12-31"
                                                        value={normalizePauseDate(row.startDate)}
                                                        onChange={(e) => handlePauseRowChange(row.id, 'startDate', e.target.value)}
                                                        className="border-gray-300 rounded-md shadow-sm py-2 px-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                                    />
                                                    <input
                                                        type="date"
                                                        min="1900-01-01"
                                                        max="2099-12-31"
                                                        value={normalizePauseDate(row.endDate)}
                                                        onChange={(e) => handlePauseRowChange(row.id, 'endDate', e.target.value)}
                                                        className="border-gray-300 rounded-md shadow-sm py-2 px-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                                    />
                                                    <button
                                                        type="button"
                                                        onClick={() => handleRemovePauseRow(row.id)}
                                                        className="text-xs text-red-600 hover:text-red-800"
                                                    >
                                                        {t('delete') || '削除'}
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    <p className="text-xs text-gray-500 mt-2">{t('employmentPauseHint')}</p>
                                </div>

                                <EditItem label={t('birthDate')} name="dateOfBirth" type="date" value={formData.dateOfBirth?.split('T')[0]} onChange={handleChange} />
                                <EditItem label={t('phoneNumber')} name="phoneNumber" value={formData.phoneNumber} onChange={handleChange} />
                                <JapanAddressSelector
                                    label={t('address')}
                                    name="address"
                                    value={formData.address}
                                    onChange={handleChange}
                                />
                            </div>

                            {/* 右侧照片区域 - 与氏名字段对齐 */}
                            <div className="ml-6 flex flex-col items-center space-y-2">
                                {/* 写真プレビューエリア - 3cm x 4cm */}
                                <div
                                    className="border-2 border-dashed border-gray-300 rounded-lg bg-gray-50 flex items-center justify-center overflow-hidden"
                                    style={{ width: '113px', height: '151px' }}
                                >
                                    {photoPreviewUrl ? (
                                        <img
                                            src={photoPreviewUrl}
                                            alt="Preview"
                                            className="w-full h-full object-cover"
                                        />
                                    ) : (
                                        <div className="text-center p-2">
                                            <svg className="w-8 h-8 text-gray-400 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                            </svg>
                                            <p className="text-xs text-gray-500">{t('noPhoto') || '写真なし'}</p>
                                        </div>
                                    )}
                                </div>

                                {/* Size information */}
                                <p className="text-xs text-gray-600">3cm × 4cm</p>

                                {/* Photo upload and download buttons */}
                                <div className="flex space-x-2">
                                    <label className="cursor-pointer">
                                        <input
                                            type="file"
                                            accept="image/*"
                                            onChange={handlePhotoChange}
                                            className="hidden"
                                        />
                                        <span className="text-xs bg-blue-600 text-white px-3 py-1.5 rounded hover:bg-blue-700 transition-colors">
                                            アップロード
                                        </span>
                                    </label>

                                    {existingPhoto && (
                                        <>
                                            <button
                                                type="button"
                                                onClick={() => attachmentPreview.openPreview(existingPhoto.id, existingPhoto.fileName || 'photo.jpg', existingPhoto.fileType)}
                                                className="text-xs bg-blue-600 text-white px-3 py-1.5 rounded hover:bg-blue-700 transition-colors"
                                            >
                                                {t('viewOnline')}
                                            </button>
                                            <button
                                                type="button"
                                                onClick={handleDownloadPhoto}
                                                className="text-xs bg-green-600 text-white px-3 py-1.5 rounded hover:bg-green-700 transition-colors"
                                            >
                                                ダウンロード
                                            </button>
                                        </>
                                    )}
                                </div>

                                {(photoPreviewUrl || existingPhoto) && (
                                    <button
                                        type="button"
                                        onClick={handleDeletePhoto}
                                        className="text-xs text-red-600 hover:text-red-800"
                                    >
                                        {t('delete') || '削除'}
                                    </button>
                                )}
                            </div>
                        </div>

                        <div className="space-y-4 mt-6">

                            <div className="border-t pt-4 mt-2"><h4 className="font-semibold text-gray-800">{t('clientManagement.title')}</h4></div>
                            <ClientSelectionFields
                                value={formData}
                                onChange={handleClientSelectionChange}
                            />

                            <div className="border-t pt-4 mt-2"><h4 className="font-semibold text-gray-800">{t('costs')}</h4></div>
                            {isSpecifiedSkill2 ? (
                                <>
                                    <div className="rounded-md border border-indigo-100 bg-indigo-50/70 p-3 text-sm text-indigo-700">
                                        <div className="font-semibold text-indigo-800">特定技能2号</div>
                                        <div>初期費用・管理費は統計対象外です。紹介費のみ登録できます。</div>
                                    </div>
                                    <EditItem
                                        label={t('introductionFee')}
                                        name="introductionFee"
                                        type="number"
                                        value={formData.introductionFee ?? ''}
                                        onChange={handleChange}
                                    />
                                </>
                            ) : (
                                <>
                            <label className="flex items-center gap-2 text-sm text-gray-700">
                                <input
                                    type="checkbox"
                                    name="initialCostNotCharged"
                                    checked={Boolean(formData.initialCostNotCharged)}
                                    onChange={handleChange}
                                    className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                />
                                <span>{t('initialCostNotCharged')}</span>
                            </label>
                            <EditItem
                                label={t('initialCost')}
                                name="initialCost"
                                type="number"
                                value={formData.initialCost ?? ''}
                                onChange={handleChange}
                                disabled={Boolean(formData.initialCostNotCharged)}
                            />
                            <label className="flex items-center gap-2 text-sm text-gray-700 mt-2">
                                <input
                                    type="checkbox"
                                    name="managementFeeNotCharged"
                                    checked={Boolean(formData.managementFeeNotCharged)}
                                    onChange={handleChange}
                                    className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                />
                                <span>{t('managementFeeNotCharged')}</span>
                            </label>
                            {formData.managementFeeNotCharged && (
                                <p className="text-xs text-gray-500 mt-1">{t('managementFeeNotChargedHint')}</p>
                            )}
                            <SelectItem
                                label={t('billingPlanType')}
                                name="billingPlanType"
                                value={formData.billingPlanType || '通常'}
                                onChange={handleChange}
                                options={billingPlanTypeOptions}
                                optionType="billingPlanType"
                                t={t}
                                getTranslatedOption={getTranslatedOption}
                                disabled={Boolean(formData.managementFeeNotCharged)}
                            />

                            {!formData.managementFeeNotCharged && (formData.billingPlanType === '通常' || formData.billingPlanType === '初期費+Nヶ月管理費' || !formData.billingPlanType) && (
                                <EditItem label={t('managementFee')} name="managementFee" type="number" value={formData.managementFee} onChange={handleChange} />
                            )}

                            {!formData.managementFeeNotCharged && formData.billingPlanType === '通常' && (
                                <>
                                    <EditItem label={t('firstMonthManagementFee')} name="firstMonthManagementFee" type="number" value={formData.firstMonthManagementFee} onChange={handleChange} />
                                    <p className="text-xs text-gray-500 mt-1">{t('firstMonthManagementFeeHint')}</p>
                                </>
                            )}

                            {!formData.managementFeeNotCharged && formData.billingPlanType === '初期費+Nヶ月管理費' && (
                                <EditItem label={t('includedManagementMonths')} name="includedManagementMonths" type="number" value={formData.includedManagementMonths ?? ''} onChange={handleChange} />
                            )}

                            {!formData.managementFeeNotCharged && formData.billingPlanType === '段階管理費' && (
                                <>
                                    <EditItem label={t('tier1Months')} name="tier1Months" type="number" value={formData.tier1Months ?? ''} onChange={handleChange} />
                                    <EditItem label={t('tier1ManagementFee')} name="tier1ManagementFee" type="number" value={formData.tier1ManagementFee ?? ''} onChange={handleChange} />
                                    <EditItem label={t('tier2Months')} name="tier2Months" type="number" value={formData.tier2Months ?? ''} onChange={handleChange} />
                                    <EditItem label={t('tier2ManagementFee')} name="tier2ManagementFee" type="number" value={formData.tier2ManagementFee ?? ''} onChange={handleChange} />
                                </>
                            )}
                                </>
                            )}

                            <EditItem label={t('contractEndDate')} name="contractEndDate" type="date" value={formData.contractEndDate?.split('T')[0]} onChange={handleChange} />

                            <div className="border-t pt-4 mt-2"><h4 className="font-semibold text-gray-800">{t('attachments')}</h4></div>
                            <div>
                                <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                                    <label className="inline-flex cursor-pointer items-center justify-center rounded-full bg-indigo-50 px-4 py-2 text-sm font-semibold text-indigo-600 hover:bg-indigo-100">
                                        <input type="file" multiple onChange={handleFileChange} className="hidden" />
                                        {t('selectFiles')}
                                    </label>
                                    <span className="text-sm text-gray-500">
                                        {filesToUpload.length > 0 ? t('selectedFilesCount', { count: filesToUpload.length }) : t('noFilesSelected')}
                                    </span>
                                </div>
                                {existingAttachments.length > 0 && (
                                    <div className="mt-4">
                                        <p className="text-sm font-medium text-gray-700">{t('attachments')}:</p>
                                        <ul className="list-disc list-inside mt-2">
                                            {existingAttachments.map(att => (
                                                <li key={att.id} className="text-sm text-gray-600 flex justify-between items-center">
                                                    <span>{att.fileName}</span>
                                                    <div className="ml-4 flex items-center gap-4">
                                                        <button type="button" onClick={() => attachmentPreview.openPreview(att.id, att.fileName, att.fileType)} className="text-blue-500 hover:underline">{t('viewOnline')}</button>
                                                        <button type="button" onClick={() => handleDownloadAttachment(att.id, att.fileName)} className="text-blue-500 hover:underline">{t('download')}</button>
                                                        <button type="button" onClick={() => handleDeleteAttachment(att.id)} className="text-red-500 hover:underline">{t('delete')}</button>
                                                    </div>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {error && (
                        <div className="px-6 py-3 bg-error-50 border-t border-error-200">
                            <p className="text-sm text-error-700 flex items-center">
                                <svg className="w-4 h-4 mr-2 text-error-500" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                </svg>
                                {error}
                            </p>
                        </div>
                    )}

                    <div className="modal-footer bg-gray-50 px-6 py-3 border-t">
                        <div className="form-buttons flex justify-end space-x-4">
                            <button
                                type="button"
                                onClick={onClose}
                                className="px-4 py-2 bg-gray-100 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500"
                            >
                                {t('cancel')}
                            </button>
                            <button
                                type="submit"
                                disabled={isLoading}
                                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 active:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {isLoading ? (
                                    <div className="flex items-center space-x-2">
                                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                        <span>{t('saving')}</span>
                                    </div>
                                ) : (
                                    t('save')
                                )}
                            </button>
                        </div>
                    </div>
                </form>
            </div>
        </div>
        <AttachmentPreviewModal
            previewState={attachmentPreview.previewState}
            onClose={attachmentPreview.closePreview}
        />
        </>
    );
}
